import { useNavigate } from 'react-router-dom';
import { useAlertsBoard } from '../lib/alerts-board';

/**
 * AlertIndicator — the ADR-071 navbar critical indicator (A1).
 *
 * Supersedes and folds in `VendorAlertBell` (D7.4): vendor alerts now flow
 * through the general alert model, so there is ONE navbar alert affordance driven
 * by the aggregated board, not a vendor bell plus separate signals.
 *
 * CRITICAL-ONLY (A1): lights only when there are active, unacknowledged
 * `critical` alerts across ALL sources. Warning/info accrue to the dashboard
 * widget and the per-source surfaces but NEVER escalate the navbar — the navbar
 * signal must mean "something needs attention now". When the count is zero AND
 * every zone answered it renders nothing, so a healthy navbar is pixel-identical
 * to today (ADR-053). An auto-cleared alert (AM3) never lights it.
 *
 * Reads the ONE shared alerts board (lib/alerts-board.tsx — quality register Q1), so its number
 * is the Alerts page's Critical card and Critical pill by construction.
 *
 * ⭐ PARTIAL, NEVER A SILENT LOWER NUMBER (Q2, 30 October 2026). When a zone did not answer, its
 * criticals are not in the count, and "0" or "1" would read as the whole truth. The indicator
 * then shows even at zero, amber-edged, with "partial" and the zone named in its accessible
 * label — an operator (or an agent reading the page) can tell an undercount from a true count.
 * Click → /admin/alerts.
 */
export function AlertIndicator() {
  const { status, counts, failedZones } = useAlertsBoard();
  const navigate = useNavigate();

  if (status !== 'loaded') return null;
  const count = counts.critical;
  const partial = failedZones.length > 0;
  if (count === 0 && !partial) return null; // healthy → no indicator (pixel-identical to today)

  const counted = `${count} critical alert${count === 1 ? '' : 's'}`;
  const missing = `${failedZones.join(' and ')} did not answer`;
  const label = partial
    ? `${counted} counted — partial: ${missing}, so its alerts are not included — view alerts`
    : `${counted} — view alerts`;

  return (
    <button
      type="button"
      className={`alert-indicator${partial ? ' alert-indicator-partial' : ''}${count === 0 ? ' alert-indicator-zero' : ''}`}
      aria-label={label}
      title={partial ? `Partial — ${missing}. ${counted} in the zones that answered.` : `${count} critical across all sources — click for Alerts`}
      data-partial={partial ? 'true' : undefined}
      onClick={() => navigate('/admin/alerts')}
    >
      <span className="alert-indicator-dot" aria-hidden="true" />
      Alerts
      <span className="alert-indicator-count">{count}</span>
      {partial && <span className="alert-indicator-partial-tag" aria-hidden="true">partial</span>}
    </button>
  );
}
